/**
 * Order Validation Service
 * 
 * This service provides centralized validation logic for order operations,
 * used by both the OrderForm for new positions and the ClosePositionModal for 
 * closing existing positions.
 */

import { getInstrumentsBySymbol } from '../websocketApi/instruments';

/**
 * Cached instrument details to avoid redundant API calls
 * Key: symbol, Value: instrument details
 */
let instrumentCache = {};

/**
 * Fetch instrument details by symbol, with caching
 * @param {string} symbol - Instrument symbol
 * @returns {Promise<Object>} Promise resolving to instrument details
 */
export const getInstrumentDetails = async (symbol) => {
  // Return from cache if available
  if (instrumentCache[symbol]) {
    return instrumentCache[symbol];
  }
  
  try {
    const instruments = await getInstrumentsBySymbol(symbol);
    
    if (instruments && instruments.length > 0) {
      // Store in cache for future use
      const details = instruments[0];
      instrumentCache[symbol] = details;
      return details;
    }
    
    // Check window cache as fallback (some components might set this)
    if (window.__instrumentsData && Array.isArray(window.__instrumentsData)) {
      // First try exact match
      let details = window.__instrumentsData.find(item => item.symbol === symbol);
      
      // If not found, try partial match
      if (!details && symbol.includes('.')) {
        const parts = symbol.split('.');
        const shortSymbol = parts[parts.length - 1];
        
        details = window.__instrumentsData.find(
          item => item.symbol && (item.symbol.includes(shortSymbol) || shortSymbol.includes(item.symbol))
        );
        
        if (details) {
          console.log(`Found instrument details in window cache for ${symbol} using partial match`);
          instrumentCache[symbol] = details;
          return details;
        }
      } else if (details) {
        console.log(`Found instrument details in window cache for ${symbol}`);
        instrumentCache[symbol] = details;
        return details;
      }
    }
    
    console.warn(`No instrument details found for ${symbol}`);
    return null;
  } catch (error) {
    console.error(`Error fetching instrument details for ${symbol}:`, error);
    return null;
  }
};

/**
 * Clear the instrument cache 
 * Useful when instrument data might have changed
 */
export const clearInstrumentCache = () => {
  instrumentCache = {};
};

/**
 * Get min order size from instrument details using the exact property name
 * @param {Object} instrumentDetails - Instrument details object
 * @returns {number|null} Min order size or null if not available
 */
export const getMinOrderSize = (instrumentDetails) => {
  if (!instrumentDetails) return null;
  
  // Use only the exact property name min_Order_Size
  if (instrumentDetails.min_Order_Size) {
    return parseFloat(instrumentDetails.min_Order_Size);
  }
  
  console.warn(`No min_Order_Size found for ${instrumentDetails.symbol}`);
  return null;
};

/**
 * Get contract size from instrument details using the exact property name
 * @param {Object} instrumentDetails - Instrument details object
 * @returns {number|null} Contract size or null if not available
 */
export const getContractSize = (instrumentDetails) => {
  if (!instrumentDetails) return null;
  
  // Use only the exact property name contract_Size
  if (instrumentDetails.contract_Size) {
    return parseFloat(instrumentDetails.contract_Size);
  }
  
  console.warn(`No contract_Size found for ${instrumentDetails.symbol}`);
  return null;
};

/**
 * Calculate display quantity (what user sees) using min_Order_Size * contract_Size
 * @param {Object} instrumentDetails - Instrument details object
 * @returns {number|null} Display quantity or null if not available
 */
export const getDisplayMinQuantity = (instrumentDetails) => {
  const minOrderSize = getMinOrderSize(instrumentDetails);
  const contractSize = getContractSize(instrumentDetails);
  
  if (minOrderSize === null || contractSize === null) {
    console.warn(`Missing min_Order_Size or contract_Size for ${instrumentDetails?.symbol}`);
    return null;
  }
  
  return minOrderSize * contractSize;
};

/**
 * Calculate display max quantity using max_Order_Size * contract_Size
 * @param {Object} instrumentDetails - Instrument details object
 * @returns {number|null} Display quantity or null if not available
 */
export const getDisplayMaxQuantity = (instrumentDetails) => {
  if (!instrumentDetails) return null;
  
  // Use only the exact property name max_Order_Size
  if (instrumentDetails.max_Order_Size && instrumentDetails.contract_Size) {
    return parseFloat(instrumentDetails.max_Order_Size) * parseFloat(instrumentDetails.contract_Size);
  }
  
  console.warn(`Missing max_Order_Size or contract_Size for ${instrumentDetails?.symbol}`);
  return null;
};

/**
 * Check if we have valid order size limits for an instrument
 * @param {Object} instrumentDetails - Instrument details object
 * @returns {boolean} True if valid limits are available
 */
export const hasValidOrderSizeLimits = (instrumentDetails) => {
  return getMinOrderSize(instrumentDetails) !== null && 
         getContractSize(instrumentDetails) !== null;
};

/**
 * Round quantity to be a multiple of min display quantity
 * @param {number} displayQuantity - Display quantity to round
 * @param {number} displayMinQuantity - Minimum display quantity
 * @returns {number} Rounded display quantity
 */
export const roundToValidQuantity = (displayQuantity, displayMinQuantity) => {
  if (displayMinQuantity === null) return displayQuantity;
  return Math.round(displayQuantity / displayMinQuantity) * displayMinQuantity;
};

/**
 * Validate order quantity against min/max limits
 * @param {number} quantity - Quantity to validate
 * @param {Object} instrumentDetails - Instrument details object
 * @returns {Object} Validation result {isValid, errorMessage}
 */
export const validateOrderQuantity = (quantity, instrumentDetails) => {
  if (!instrumentDetails) {
    return {
      isValid: false,
      errorMessage: 'Instrument details not available'
    };
  }
  
  if (!hasValidOrderSizeLimits(instrumentDetails)) {
    return {
      isValid: false,
      errorMessage: 'Order size limits not available for this instrument'
    };
  }
  
  const displayMinQuantity = getDisplayMinQuantity(instrumentDetails);
  const displayMaxQuantity = getDisplayMaxQuantity(instrumentDetails);
  
  if (displayMinQuantity === null || displayMaxQuantity === null) {
    return {
      isValid: false,
      errorMessage: 'Min/max quantity limits not available'
    };
  }
  
  const numericQuantity = parseFloat(quantity);
  
  if (isNaN(numericQuantity) || numericQuantity <= 0) {
    return {
      isValid: false,
      errorMessage: 'Please enter a valid quantity'
    };
  }
  
  if (numericQuantity < displayMinQuantity) {
    return {
      isValid: false,
      errorMessage: `Minimum order size is ${displayMinQuantity}`
    };
  }
  
  if (numericQuantity > displayMaxQuantity) {
    return {
      isValid: false,
      errorMessage: `Maximum order size is ${displayMaxQuantity}`
    };
  }
  
  return {
    isValid: true,
    errorMessage: ''
  };
};

/**
 * Format number with commas and proper decimal places
 * @param {number} num - Number to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted number string
 */
export const formatNumber = (num, decimals = 2) => {
  if (num === undefined || num === null) return 'N/A';
  return parseFloat(num).toLocaleString(undefined, { 
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

/**
 * Format PnL with color and sign
 * @param {number} pnl - P&L value
 * @returns {Object} Formatted value and isPositive flag
 */
export const formatPnL = (pnl) => {
  if (pnl === undefined || pnl === null) return { value: 'N/A', isPositive: false };
  const value = parseFloat(pnl);
  const formattedValue = Math.abs(value).toLocaleString(undefined, { 
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return {
    value: `${value >= 0 ? '+' : '-'}$${formattedValue}`,
    isPositive: value >= 0
  };
};

/**
 * Format symbol by splitting and taking the last part
 * @param {string} symbol - Full symbol string
 * @returns {string} Formatted symbol
 */
export const formatSymbol = (symbol) => {
  if (!symbol) return 'N/A';
  const parts = symbol.split('.');
  return parts[parts.length - 1];
};

/**
 * Generate order parameters for placing an order
 * @param {Object} options - Order options
 * @param {string} options.symbol - Instrument symbol
 * @param {string} options.side - Order side (buy/sell)
 * @param {string} options.orderType - Order type (market/limit)
 * @param {number} options.price - Order price
 * @param {number} options.quantity - Order quantity (display quantity)
 * @param {number} options.leverage - Leverage value
 * @param {number} options.stopLoss - Stop loss price (optional)
 * @param {number} options.takeProfit - Take profit price (optional)
 * @param {string} options.mode - Order mode (open/close)
 * @returns {Object} Order parameters to send to API
 */
export const generateOrderParams = (options) => {
  const {
    symbol,
    side,
    orderType,
    price,
    quantity,
    leverage,
    stopLoss,
    takeProfit,
    mode = 'open'
  } = options;
  
  return {
    symbol,
    side,
    orderType,
    price,
    quantity, // Use the display quantity directly (no conversion needed)
    leverage,
    ...(stopLoss ? { stopLoss } : {}),
    ...(takeProfit ? { takeProfit } : {}),
    mode
  };
}; 